import { Add, Delete, Edit, Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IPaginationData } from '../../../api/base';
import postApi from '../../../api/post.api';
import Table, { Order } from '../../../components/Table';
import { EComponent } from '../../../models/base';
import { EScope, IPostModel } from '../../../models/post.model';
import theme from '../../../theme';
import { PostItemImage } from '../../styled';

export default function GreenHubListPage() {
  const [component, setComponent] = useState<EComponent | 'all'>('all');
  const [dataSource, setDataSource] = useState<IPaginationData<IPostModel>>({
    limit: 10,
    page: 1,
    total: 0,
    total_page: 0,
    records: [],
  });
  const [loading, setLoading] = useState(false);
  const [[orderBy, order], setOrder] = useState<
    [string | undefined, Order | undefined]
  >([undefined, 'asc']);

  const [filter, setFilter] = useState({
    keyword: '',
    page: 1,
    limit: 10,
  });

  const [currDeletedId, setCurrDeletedId] = useState<string | undefined>();
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    postApi
      .getList({
        ...filter,
        component: component !== 'all' ? component : undefined,
        scope: EScope.GreenHub,
        order: order?.toUpperCase() as 'ASC' | 'DESC',
        sort_by: orderBy,
      })
      .then((res) => {
        setDataSource(res.data);
      })
      .catch((e) => {
        toast.error(e.message ?? e.toString());
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter, component, order, orderBy]);

  useEffect(loadData, [loadData]);

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Typography variant="h3">Green Hub</Typography>
        <div
          style={{
            display: 'flex',
            gap: theme.spacing(4),
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const formData = new FormData(e.currentTarget);
              setFilter({
                ...filter,
                page: 1,
                keyword: (formData.get('search') || '').toString(),
              });
            }}
          >
            <TextField
              placeholder="Search"
              variant="outlined"
              size="small"
              name="search"
              sx={{
                '& .MuiInputBase-root': {
                  pr: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      type="submit"
                      sx={{ minWidth: 48, borderRadius: 0 }}
                    >
                      <Search />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
          <Link
            to={{
              pathname: 'create',
              search: queryString.stringify({
                component: component !== 'all' ? component : undefined,
              }),
            }}
          >
            <Button variant="contained" color="success" startIcon={<Add />}>
              Create new
            </Button>
          </Link>
        </div>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          variant="fullWidth"
          value={component}
          onChange={(ev, v) => setComponent(v)}
          sx={{
            '& .MuiTab-root': {
              fontWeight: 700,
              fontSize: 18,
            },
          }}
        >
          <Tab label="All" value="all" />
          {Object.values(EComponent).map((t) => (
            <Tab key={t} label={_.capitalize(_.words(t).join(' '))} value={t} />
          ))}
        </Tabs>
      </Box>
      <Table
        onOrderChange={(orderBy, order) => setOrder([orderBy!, order!])}
        order={order}
        orderBy={orderBy}
        loading={loading}
        pagination={{
          limit: dataSource.limit,
          total: dataSource.total,
          page: dataSource.page - 1,
          onPageChange: (event, page) => {
            setFilter({
              ...filter,
              page: page + 1,
            });
          },
          onLimitChange: (ev) => {
            setFilter({
              ...filter,
              limit: +ev.target.value,
              page: 1,
            });
          },
        }}
        columns={[
          {
            key: 'order',
            title: 'Order',
            dataKey: 'order',
            sortable: true,
            render: (data) => data.order,
          },
          {
            key: 'picture',
            title: 'Picture',
            dataKey: 'picture',
            render: (data) => (
              <PostItemImage
                src={
                  data.picture
                    ? `${process.env.REACT_APP_API_URI}${data.picture}`
                    : undefined
                }
                alt={data?.id}
              />
            ),
          },
          {
            key: 'title',
            title: 'Name',
            dataKey: 'title_en',
            render: (data) => (
              <>
                <div>{data.title_vi}</div>
                <div>{data.title_en}</div>
              </>
            ),
          },
          {
            key: 'type',
            title: 'Type',
            dataKey: 'type',
          },
          {
            key: 'category',
            title: 'Start-up',
            dataKey: 'category',
            sortable: true,
            render: (data) => data.category?.title_en,
          },
          {
            key: 'tags',
            title: 'Investor',
            dataKey: 'tags',
            sortable: true,
            render: (data) =>
              data.tags?.map((tag) => (
                <span key={tag.id}> {tag.name_en} </span>
              )),
          },
          {
            key: 'published_at',
            title: 'Published at',
            dataKey: 'published_at',
            sortable: true,
            render: (data) =>
              data.published_at
                ? moment(data.published_at).format('DD/MM/YYYY HH:mm:ss')
                : '-',
          },
          {
            key: 'created_at',
            title: 'Created at',
            dataKey: 'created_at',
            sortable: true,
            render: (data) =>
              moment(data.created_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'updated_at',
            title: 'Updated at',
            dataKey: 'updated_at',
            sortable: true,
            render: (data) =>
              moment(data.updated_at).format('DD/MM/YYYY HH:mm:ss'),
          },
          {
            key: 'visible',
            title: 'Visible',
            dataKey: 'visible',
            sortable: true,
            render: (data) => (
              <Checkbox
                onChange={(ev, value) => {
                  const idx = dataSource.records.indexOf(data);
                  postApi.update(data.id, { visible: value }).then((res) => {
                    const records = Array.from(dataSource.records);
                    records[idx] = {
                      ...data,
                      visible: res.data.visible,
                    };
                    setDataSource({
                      ...dataSource,
                      records,
                    });
                  });
                }}
                checked={data.visible}
                readOnly
              />
            ),
          },
          {
            key: 'action',
            title: '',
            dataKey: 'id',
            render: (data) => (
              <>
                <Link to={data.id}>
                  <IconButton>
                    <Edit />
                  </IconButton>
                </Link>
                <IconButton onClick={() => setCurrDeletedId(data.id)}>
                  <Delete />
                </IconButton>
              </>
            ),
          },
        ]}
        dataSource={dataSource.records}
        rowKey={(data) => data.id}
      />
      <Dialog
        open={!!currDeletedId}
        onClose={() => setCurrDeletedId(undefined)}
      >
        <DialogContent>
          <DialogContentText color={'black'}>
            This action cannot be revert. Do you want to delete this post?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            disabled={deleting}
            onClick={() => setCurrDeletedId(undefined)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={deleting}
            onClick={() => {
              if (currDeletedId) {
                setDeleting(true);
                postApi
                  .remove(currDeletedId)
                  .then(() => {
                    loadData();
                    toast.success('Delete post successful');
                  })
                  .catch((e) => {
                    toast.error(e.message ?? e.toString());
                  })
                  .finally(() => {
                    setCurrDeletedId(undefined);
                    setDeleting(false);
                  });
              }
            }}
            color="error"
          >
            {deleting ? <CircularProgress size={16} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
